// Vendor imports
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
const ReactCrop = require('react-image-crop').default
// Shared imports
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')

const bem = BemEntity('UploadImageModalPreview')

class UploadImageModalPreview extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      crop: { x: 0, y: 0 },
      minWidth: null,
      minHeight: null,
      src: null
    }

    this.onCropComplete = this.onCropComplete.bind(this)
    this.onCropChange = this.onCropChange.bind(this)
  }

  // React Component lifecycle methods

  componentDidMount() {
    if (this.props.image) {
      this.setState(this.computeInitialState())
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.image !== this.props.image) {
      this.setState(this.computeInitialState())
    }
  }

  // State handling methods

  computeInitialState() {
    const { props } = this
    const { image } = props
    // Calculate aspect crop
    const aspectRatio = props.resizeWidth / props.resizeHeight
    const aspectWidth = Math.round(image.height * aspectRatio)
    const aspectHeight = Math.round(image.width / aspectRatio)
    // Calculate center
    let xPixels = 0
    let yPixels = 0
    if (aspectWidth < image.width) {
      xPixels = Math.round((image.width / 2) - (aspectWidth / 2))
    } else if (aspectHeight < image.height) {
      yPixels = Math.round((image.height / 2) - (aspectHeight / 2))
    }
    // Calculate minimum sizing
    let minWidth = null
    if (props.minWidth) {
      minWidth = (props.minWidth / image.width) * 100
    }
    let minHeight = null
    if (props.minHeight) {
      minHeight = (props.minHeight / image.height) * 100
    }
    // Convert pixels to percentages
    const xPercent = (xPixels / image.width) * 100
    const yPercent = (yPixels / image.height) * 100
    const crop = {
      x: xPercent,
      y: yPercent,
      width: 100,
      aspect: aspectRatio
    }
    return { crop, minWidth, minHeight, src: image.src }
  }

  clampCrop(crop) {
    const { minWidth, minHeight } = this.state
    const clamped = Object.assign({}, crop)
    // Clamp width
    clamped.width = Math.max(0, Math.min(100, clamped.width))
    if (minWidth) {
      clamped.width = Math.max(clamped.width, minWidth)
    }
    // Clamp height
    clamped.height = Math.max(0, Math.min(100, clamped.height))
    if (minHeight) {
      clamped.height = Math.max(clamped.height, minHeight)
    }
    // Clamp X
    clamped.x = Math.max(0, clamped.x)
    if (clamped.x + clamped.width > 100) {
      clamped.x = 100 - clamped.width
    }
    // Clamp Y
    clamped.y = Math.max(0, clamped.y)
    if (clamped.y + clamped.height > 100) {
      clamped.y = 100 - clamped.height
    }
    return clamped
  }

  denormalizeCrop(cropPercent) {
    const { image } = this.props
    return {
      x: Math.round(image.width * (cropPercent.x / 100)),
      y: Math.round(image.height * (cropPercent.y / 100)),
      width: Math.round(image.width * (cropPercent.width / 100)),
      height: Math.round(image.height * (cropPercent.height / 100))
    }
  }

  // ReactCrop callback methods

  onCropComplete(crop) {
    this.props.onCropped(this.denormalizeCrop(crop))
  }

  onCropChange(crop) {
    this.setState(() => ({ crop: this.clampCrop(crop) }))
  }

  // Render methods

  renderCrop() {
    if (!this.state.src) {
      return null
    }
    return tag(ReactCrop, {
      crop: this.state.crop,
      keepSelection: true,
      minHeight: this.state.minHeight,
      minWidth: this.state.minWidth,
      src: this.state.src,
      onComplete: this.onCropComplete,
      onChange: this.onCropChange
    })
  }

  render() {
    return tag('section', {
      className: classNames(
        this.props.className,
        bem.block()
      )
    },
      this.renderCrop()
    )
  }
}

const imagePropType = typeof Image === 'function'
? PropTypes.instanceOf(Image)
: PropTypes.shape({
  height: PropTypes.number,
  src: PropTypes.string,
  width: PropTypes.number
})

UploadImageModalPreview.propTypes = {
  // Values
  className: PropTypes.string,
  image: imagePropType,
  minHeight: PropTypes.number,
  minWidth: PropTypes.number,
  resizeWidth: PropTypes.number,
  resizeHeight: PropTypes.number,
  // Callbacks
  onCropped: PropTypes.func
}

module.exports = UploadImageModalPreview
